<script>
	// NPM Modules
	import collect from 'collect.js';
	import { createEventDispatcher, tick } from 'svelte';
	// Stores
	import SportStore from '@/Stores/Sport/SportStore';
	import DerivedMarketGroupStore from '@/Stores/Sport/DerivedMarketGroupStore';
	import DerivedMarketTemplateStore from '@/Stores/Sport/DerivedMarketTemplateStore.js';
	import MarketTypeStore from '@/Stores/Sport/MarketTypeStore.js';
	// Components
	import Icon from '@/Components/Icon.svelte';
	import PillMultiSelect from '@/Components/PillMultiSelect.svelte';
	import LoadingButton from '@/Components/LoadingButton.svelte';
	import TextInput from '@/Components/TextInput.svelte';
	import PillTextInput from '@/Components/PillTextInput.svelte';
	import SelectInput from '@/Components/SelectInput.svelte';
	// Services
	import { notify } from '@/Services/Notify';

	const dispatch = createEventDispatcher();

	export let sport;

	let sending = false;
	let selectedDerivedMarketGroup;
	let selectedSport = sport.id;

	let marketTypeIdSelector = null;

	$: selectedSport && selectedSportChanged();
	$: derivedMarketGroups = $DerivedMarketGroupStore && $DerivedMarketGroupStore.where('id', '!=', 'create').all();
	$: derivedMarketTemplates = $DerivedMarketTemplateStore && $DerivedMarketTemplateStore.all();

	async function selectedSportChanged() {
		await DerivedMarketGroupStore.getDerivedMarketGroupsForSport(sport.id);
		await DerivedMarketTemplateStore.getDerivedMarketTemplatesForSport(sport.id);
		await MarketTypeStore.getMarketTypesForSport(sport.id);
	}

	function addToSelectedDerivedMarket(derivedMarket) {
		selectedDerivedMarketGroup.derived_market_templates.data =
			selectedDerivedMarketGroup.derived_market_templates.data.push({
				id: derivedMarket.id,
				name: derivedMarket.name,
				sport: {
					id: derivedMarket.sport.id,
					name: derivedMarket.sport.name
				}
			});
	}

	function removeFromSelectedDerivedMarketGroup(derivedMarketId) {
		selectedDerivedMarketGroup.derived_market_templates.data =
			selectedDerivedMarketGroup.derived_market_templates.data.reject((derivedMarketTemplates) => {
				return derivedMarketTemplates.id == derivedMarketId;
			});
	}

	function removeMarketTypeFromAddList(marketType) {
		let indexToRemove;
		for (const index in selectedDerivedMarketGroup.market_types) {
			if (selectedDerivedMarketGroup.market_types[index].toString() === marketType.id.toString()) {
				indexToRemove = index;
				break;
			}
		}
		if (indexToRemove) {
			selectedDerivedMarketGroup.market_types.splice(indexToRemove, 1);
		}

		selectedDerivedMarketGroup.market_types = selectedDerivedMarketGroup.market_types; // Trick reactivity
	}

	function removeAllMarketsFromAddList() {
		let indexToRemove;
		for (const index in selectedDerivedMarketGroup.market_types) {
			if (selectedDerivedMarketGroup.market_types[index].toString() === 'all') {
				indexToRemove = index;
				break;
			}
		}
		if (indexToRemove) {
			selectedDerivedMarketGroup.market_types.splice(indexToRemove, 1);
		}

		selectedDerivedMarketGroup.market_types = selectedDerivedMarketGroup.market_types; // Trick reactivity
	}

	function createDerivedMarketGroup(derivedMarket) {
		if (derivedMarket.market_types.length == 0) {
			notify({
				type: 'danger',
				title: 'Error!',
				message: 'You have not added any market types!',
				timeout: 3
			});
			return false;
		}
		sending = true;
		DerivedMarketGroupStore.createDerivedMarketGroup(sport.id, derivedMarket.preparePayload())
			.then((derivedMarketGroup) => {
				notify({
					type: 'success',
					title: 'Derived market group successfully created',
					message: `${derivedMarket.name} successfully created!`,
					timeout: 3
				});
				selectedDerivedMarketGroup = derivedMarketGroup;
			})
			.catch(() => {
				notify({
					type: 'danger',
					title: 'Error!',
					message: 'Problem creating derived market group',
					timeout: 3
				});
			})
			.finally(() => (sending = false));
	}

	function saveDerivedMarketGroup(derivedMarket) {
		if (derivedMarket.market_types.length == 0) {
			notify({
				type: 'danger',
				title: 'Error!',
				message: 'You have not added any market types!',
				timeout: 3
			});
			return false;
		}
		sending = true;
		DerivedMarketGroupStore.updateDerivedMarketGroup(derivedMarket)
			.then(() => {
				notify({
					type: 'success',
					title: 'Derived market group successfully updated',
					message: `${derivedMarket.name} successfully updated!`,
					timeout: 3
				});
			})
			.catch(() => {
				notify({
					type: 'danger',
					title: 'Error!',
					message: 'Problem updating derived market group',
					timeout: 3
				});
			})
			.finally(() => (sending = false));
	}

	function getMarketTypeListText(marketTypes) {
		if (!marketTypes) {
			return '';
		}
		let marketTypesText = '';
		for (const marketType of marketTypes) {
			if (marketTypesText !== '') {
				marketTypesText = marketTypesText + ', ';
			}
			if (marketType == 'all') {
				marketTypesText = marketTypesText + 'All market types';
			} else {
				marketTypesText = marketTypesText + ($MarketTypeStore.firstWhere('id', marketType)?.label ?? '');
			}
		}
		return marketTypesText;
	}
</script>

{#if !selectedDerivedMarketGroup}
	<div>
		<div class="flex justify-end items-center mt-1">
			<button
				class="btn btn-primary"
				on:click={() => {
					DerivedMarketGroupStore.newDerivedMarketGroup(sport);
					selectedDerivedMarketGroup = $DerivedMarketGroupStore.firstWhere('id', 'create');
					dispatch('selected');
				}}>Create new derived market group</button
			>
		</div>
		<table class="w-full whitespace-nowrap mt-3">
			<thead>
				<tr class="text-xs text-left text-gray-500 uppercase">
					<th class="px-6 pt-3 pb-2 font-medium">Name</th>
					<th class="w-px px-6 pt-3 pb-2 font-medium text-left">Market Types</th>
					<th class="w-px px-6 pt-3 pb-2 font-medium text-left">Derived Markets</th>
					<th class="w-px px-6 pt-3 pb-2 font-medium text-left">Sport</th>
					<th class="w-px px-6 pt-3 pb-2 font-medium text-right" />
				</tr>
			</thead>
			<tbody class="bg-white">
				{#each derivedMarketGroups as derivedMarketGroup (derivedMarketGroup.id)}
					<tr
						on:click={() => {
							selectedDerivedMarketGroup = derivedMarketGroup;
							dispatch('selected');
						}}
						class="cursor-pointer hover:bg-gray-100 focus-within:bg-gray-100"
					>
						<td class="p-4 px-6 border-t">
							<div class="flex items-center">
								<div class="mr-2 font-semibold">{derivedMarketGroup.name}</div>
							</div>
						</td>

						<td class="px-6 border-t">
							{$MarketTypeStore && getMarketTypeListText(derivedMarketGroup.market_types)}
						</td>
						<td class="px-6 border-t"
							>{derivedMarketGroup.derived_market_templates.data.count() == 1
								? derivedMarketGroup.derived_market_templates.data.count() + ' market'
								: derivedMarketGroup.derived_market_templates.data.count() + ' markets'}
						</td>
						<td class="w-px px-6 border-t">
							<div class="px-2 py-1 bg-green-500 rounded-md text-light">
								{derivedMarketGroup.sport.name}
							</div>
						</td>
						<td class="w-px px-6 border-t">
							<Icon name="chevron-right" classes="block w-4 h-4 text-gray-400 inline" />
						</td>
					</tr>
				{:else}
					<tr>
						<td class="px-6 py-4 text-center border-t" colspan="9">
							No derived market groups found for {sport.name}.
						</td>
					</tr>
				{/each}
			</tbody>
		</table>
	</div>
{:else}
	<div class="w-full mt-3">
		<div class="sticky top-0 z-10 flex py-2">
			<div class="flex flex-col flex-grow">
				<div class="flex justify-between">
					<div>
						<div class="flex flex-row items-center">
							<button
								title={'Market types list'}
								class="flex cursor-pointer hover:underline text-grey items-center"
								on:click={() => {
									selectedDerivedMarketGroup = undefined;
								}}
							>
								<Icon name="chevron-left" classes="w-2 mr-2" />
								<span class="mr-2 text-lg font-bold whitespace-nowrap">Derived Market Groups</span>
							</button>
						</div>
					</div>
					<div>
						<h2 class="pt-2 text-xl font-bold text-center">
							{#if selectedDerivedMarketGroup.id !== 'create'}
								{selectedDerivedMarketGroup.name}
							{/if}
						</h2>
					</div>
					<div>
						<div class="flex flex-row items-center">
							{#if selectedDerivedMarketGroup.id == 'create'}
								<LoadingButton
									loading={sending}
									className="text-nowrap whitespace-nowrap"
									class="btn btn-primary flex flex-row text-nowrap whitespace-nowrap flex-nowrap"
									on:click={() => createDerivedMarketGroup(selectedDerivedMarketGroup)}>Create group</LoadingButton
								>
							{:else}
								<LoadingButton
									loading={sending}
									className="btn btn-primary text-nowrap whitespace-nowrap"
									class="btn btn-primary flex flex-row text-nowrap whitespace-nowrap flex-nowrap"
									on:click={() => saveDerivedMarketGroup(selectedDerivedMarketGroup)}>Save changes</LoadingButton
								>
							{/if}
						</div>
					</div>
				</div>
			</div>
		</div>
		<div class="flex flex-col w-full text-center rounded">
			{#if $SportStore}
				<div class="flex flex-row w-full border-t border-b">
					<hr class="py-2" />
					<div class="flex flex-row w-full space-x-2">
						<div class="flex w-1/3 text-left">
							<TextInput
								name="name"
								label="Derived market group name"
								className="pb-3 w-full py-4"
								placeholder="Enter derived market group name"
								bind:value={selectedDerivedMarketGroup.name}
							/>
						</div>
						<div class="flex flex-col w-full">
							<div class="flex w-full text-left">
								<SelectInput
									label="Select market types that CAN be derived by this group"
									className="py-4 w-2/5 text-left"
									name="marketTypeId"
									disabled={sending}
									required={true}
									bind:value={marketTypeIdSelector}
									on:change={(event) => {
										if (event.target.value == 'all') {
											selectedDerivedMarketGroup.market_types.push('all');
											selectedDerivedMarketGroup.market_types = selectedDerivedMarketGroup.market_types; //trick reactivity
										} else {
											const marketType = $MarketTypeStore.firstWhere('id', event.target.value);
											if (marketType) {
												if (!selectedDerivedMarketGroup.market_types.includes(marketType.id)) {
													selectedDerivedMarketGroup.market_types.push(marketType.id);
													selectedDerivedMarketGroup.market_types = selectedDerivedMarketGroup.market_types; //trick reactivity
												}
											}
										}
										tick().then(() => {
											marketTypeIdSelector = null;
										});
									}}
								>
									<option value={null} selected disabled>Select market type</option>
									<option value="all">All market types</option>
									{#each $MarketTypeStore
										.where('rolling_type', null)
										.sortBy('name')
										.all() as marketType (marketType.id)}
										<option value={marketType.id}>
											{#if marketType.label}
												{marketType.name} - [{marketType.label}]
											{:else}
												{marketType.name}
											{/if}
										</option>
									{/each}
								</SelectInput>
								<div class="flex justify-center mt-2 flex-wrap w-full items-center">
									{#if selectedDerivedMarketGroup.market_types.includes('all')}
										<PillTextInput
											action="remove"
											name={'all'}
											disabled={sending}
											bgColour="bg-indigo-500"
											marketType="m-2"
											label={'All market types'}
											value={false}
											on:remove={() => removeAllMarketsFromAddList()}
										/>
									{/if}
									{#each $MarketTypeStore
										.whereIn('id', selectedDerivedMarketGroup.market_types)
										.all() as marketType (marketType.id)}
										<PillTextInput
											action="remove"
											name={marketType.id}
											disabled={sending}
											bgColour="bg-indigo-500"
											marketType="m-2"
											label={marketType.name}
											value={false}
											on:remove={() => removeMarketTypeFromAddList(marketType)}
										/>
									{:else}
										{#if !selectedDerivedMarketGroup.market_types.includes('all')}
											<div class="font-semibold my-4">No Market types added yet</div>
										{/if}
									{/each}
								</div>
							</div>
						</div>
					</div>
					<hr />
				</div>

				<div class="flex my-2 w-full min-h-104 bg-white rounded">
					<div class="flex w-full space-x-2">
						<div class="w-2/3">
							<div class="flex items-center w-full m-2 space-x-4">
								<div class="font-semibold">
									Derived market templates that will be created when group {selectedDerivedMarketGroup.name
										? '"' + selectedDerivedMarketGroup.name + '"'
										: ''} is used ({selectedDerivedMarketGroup?.derived_market_templates?.data?.count() ?? 0})
								</div>
								<button
									class="py-1 -mb-1 text-xs font-normal btn btn-primary-action"
									on:click={() => {
										selectedDerivedMarketGroup.derived_market_templates.data = collect([]);
									}}
								>
									Clear
								</button>
							</div>
							<div class="w-full p-1 overflow-y-auto bg-gray-200 border">
								{#if selectedDerivedMarketGroup?.derived_market_templates?.data?.count() === 0}
									<div class="py-4 mx-2 text-center">Add some derived market templates to group...</div>
								{/if}
								{#each selectedDerivedMarketGroup?.derived_market_templates?.data?.all() ?? [] as derivedMarket}
									<div class="flex items-center w-full space-x-2">
										<PillMultiSelect
											action="remove"
											on:remove={() => removeFromSelectedDerivedMarketGroup(derivedMarket.id)}
											bgColour={'bg-orange-500'}
											label={`${derivedMarket.name} [${derivedMarket.sport.name}]`}
										/>
									</div>
								{/each}
							</div>
						</div>
						<div class="w-1/3 my-2">
							<div class="flex flex-col items-center justify-start w-full mb-2">
								<div class="font-semibold">
									Available derived market templates ({`${derivedMarketTemplates.length - (selectedDerivedMarketGroup?.derived_market_templates?.data?.count() ?? 0)}/${derivedMarketTemplates.length}`})
								</div>
							</div>
							<div class="w-full p-2 overflow-y-auto bg-gray-200 border" style="max-height: 225px;">
								{#each derivedMarketTemplates as derivedMarketTemplate}
									{#if !(selectedDerivedMarketGroup?.derived_market_templates?.data
											?.pluck('id')
											.all() ?? []).includes(derivedMarketTemplate.id)}
										<div class="w-full">
											<PillMultiSelect
												on:add={() => addToSelectedDerivedMarket(derivedMarketTemplate)}
												bgColour={'bg-green-500'}
												label={derivedMarketTemplate.name}
											/>
										</div>
									{/if}
								{:else}
									No derived market templates created!
								{/each}
							</div>
						</div>
					</div>
				</div>
			{/if}
		</div>
	</div>
{/if}
